import { api } from './client';

export interface SessionIntelligenceSession {
  session_id: string;
  name: string;
  description: string;
  created: string;
  last_accessed: string;
  tab_count: number;
  category: string;
  favorite: boolean;
  tags: string[];
  thumbnail: string;
  is_active: boolean;
}

export interface SessionIntelligenceAnalytics {
  total_tabs: number;
  active_tabs: number;
  closed_tabs: number;
  distinct_domains: number;
  total_time_seconds: number;
  avg_duration: number;
}

export interface SessionIntelligencePattern {
  type: string;
  title: string;
  description: string;
}

export interface SessionIntelligencePayload {
  session: SessionIntelligenceSession;
  analytics: SessionIntelligenceAnalytics;
}

/** Response shape from POST /sessions (workspace session row). */
export interface CreatedWorkspaceSession {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  tab_count: number;
  created_at: string;
}

interface SessionListRow {
  id: string;
  is_active: boolean;
}

function ensureSessionPayload(raw: Record<string, unknown>): SessionIntelligencePayload {
  const session = (raw.session ?? {}) as Record<string, unknown>;
  const analytics = (raw.analytics ?? {}) as Record<string, unknown>;
  return {
    session: {
      session_id: String(session.session_id ?? ''),
      name: String(session.name ?? 'Untitled Session'),
      description: String(session.description ?? ''),
      created: String(session.created ?? ''),
      last_accessed: String(session.last_accessed ?? ''),
      tab_count: Number(session.tab_count ?? 0),
      category: String(session.category ?? 'General'),
      favorite: Boolean(session.favorite),
      tags: Array.isArray(session.tags) ? session.tags.map((t) => String(t)) : [],
      thumbnail: String(session.thumbnail ?? '📁'),
      is_active: Boolean(session.is_active),
    },
    analytics: {
      total_tabs: Number(analytics.total_tabs ?? 0),
      active_tabs: Number(analytics.active_tabs ?? 0),
      closed_tabs: Number(analytics.closed_tabs ?? 0),
      distinct_domains: Number(analytics.distinct_domains ?? 0),
      total_time_seconds: Number(analytics.total_time_seconds ?? 0),
      avg_duration: Number(analytics.avg_duration ?? 0),
    },
  };
}

function mapCreatedSession(raw: Record<string, unknown>): CreatedWorkspaceSession {
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? 'New Session'),
    description: String(raw.description ?? ''),
    is_active: Boolean(raw.is_active),
    tab_count: Number(raw.tab_count ?? 0),
    created_at: String(raw.created_at ?? ''),
  };
}

export const sessionIntelligenceService = {
  async createSession(params?: { name?: string; description?: string }): Promise<CreatedWorkspaceSession> {
    const response = await api.post<Record<string, unknown>>('/sessions', {
      name: params?.name?.trim() ?? '',
      description: params?.description?.trim() ?? '',
    });
    return mapCreatedSession(response.data ?? {});
  },

  async listWorkspaceSessionIds(): Promise<string[]> {
    const res = await api.get<{ sessions?: SessionListRow[] }>('/sessions');
    const rows = res.data.sessions ?? [];
    const activeFirst = [...rows].sort((a, b) => Number(b.is_active) - Number(a.is_active));
    return activeFirst.map((row) => row.id).filter(Boolean);
  },

  async getSessionIntel(sessionId: string): Promise<SessionIntelligencePayload> {
    const response = await api.get<Record<string, unknown>>(`/session-intelligence/${sessionId}`);
    return ensureSessionPayload(response.data ?? {});
  },

  async getSessionSummary(sessionId: string): Promise<{
    details: SessionIntelligenceSession;
    analytics: SessionIntelligenceAnalytics;
    patterns: SessionIntelligencePattern[];
  }> {
    const response = await api.get<Record<string, unknown>>(`/session-intelligence/${sessionId}/summary`);
    const detailsRaw = (response.data.details ?? {}) as Record<string, unknown>;
    const details = ensureSessionPayload({ session: detailsRaw, analytics: response.data.analytics as Record<string, unknown> }).session;
    const analytics = ensureSessionPayload({ session: detailsRaw, analytics: response.data.analytics as Record<string, unknown> }).analytics;
    const patterns = Array.isArray(response.data.patterns)
      ? response.data.patterns.map((row) => {
          const item = row as Record<string, unknown>;
          return {
            type: String(item.type ?? 'pattern'),
            title: String(item.title ?? 'Pattern'),
            description: String(item.description ?? ''),
          };
        })
      : [];
    return { details, analytics, patterns };
  },
};

export default sessionIntelligenceService;
